import React, { useRef, useState, useEffect } from 'react';

function getDistance(touches) {
  if (touches.length < 2) return 0;
  const [a, b] = touches;
  return Math.hypot(a.pageX - b.pageX, a.pageY - b.pageY);
}

function getCenter(touches) {
  if (touches.length < 2) return { x: touches[0].pageX, y: touches[0].pageY };
  const [a, b] = touches;
  return {
    x: (a.pageX + b.pageX) / 2,
    y: (a.pageY + b.pageY) / 2
  };
}

export default function PinchZoomPanContainer({ children, style }) {
  const containerRef = useRef(null);
  const [scale, setScale] = useState(1);
  const [translateX, setTranslateX] = useState(0);
  const [translateY, setTranslateY] = useState(0);
  const [lastDist, setLastDist] = useState(null);
  const [lastCenter, setLastCenter] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [initialScale, setInitialScale] = useState(1);

  // Calculate initial scale to fit content on screen
  useEffect(() => {
    const calculateInitialScale = () => {
      if (!containerRef.current) return;
      
      const container = containerRef.current;
      const containerRect = container.getBoundingClientRect();
      
      // Assume each image takes roughly half the container width
      // and we want them to fit within 90% of viewport
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;
      
      // Estimate content width (two images side by side)
      const estimatedContentWidth = containerRect.width;
      const estimatedContentHeight = containerRect.height;
      
      const scaleX = (viewportWidth * 0.9) / estimatedContentWidth;
      const scaleY = (viewportHeight * 0.9) / estimatedContentHeight;
      
      const fitScale = Math.min(scaleX, scaleY, 1); // Don't scale up beyond 1
      
      setInitialScale(fitScale);
      setScale(fitScale);
    };

    // Delay calculation to ensure images are loaded
    const timer = setTimeout(calculateInitialScale, 100);
    return () => clearTimeout(timer);
  }, []);

  function onTouchStart(e) {
    e.preventDefault();
    
    if (e.touches.length === 1) {
      setIsDragging(true);
      const touch = e.touches[0];
      setLastCenter({ x: touch.pageX, y: touch.pageY });
    } else if (e.touches.length === 2) {
      setIsDragging(false);
      setLastDist(getDistance(e.touches));
      setLastCenter(getCenter(e.touches));
    }
  }

  function onTouchMove(e) {
    e.preventDefault();
    
    if (e.touches.length === 1 && isDragging) {
      // Single finger drag - pan
      const touch = e.touches[0];
      const deltaX = touch.pageX - lastCenter.x;
      const deltaY = touch.pageY - lastCenter.y;
      
      setTranslateX(prev => prev + deltaX);
      setTranslateY(prev => prev + deltaY);
      setLastCenter({ x: touch.pageX, y: touch.pageY });
    } else if (e.touches.length === 2 && lastDist) {
      // Two finger pinch - zoom
      const newDist = getDistance(e.touches);
      const newCenter = getCenter(e.touches);
      
      // Calculate scale change
      let nextScale = scale * (newDist / lastDist);
      nextScale = Math.max(0.3, Math.min(nextScale, 5));
      
      // Calculate translation adjustment to zoom towards the pinch center
      const containerRect = containerRef.current.getBoundingClientRect();
      const centerX = containerRect.left + containerRect.width / 2;
      const centerY = containerRect.top + containerRect.height / 2;
      
      const scaleDelta = nextScale - scale;
      const adjustX = (centerX - newCenter.x) * scaleDelta / scale;
      const adjustY = (centerY - newCenter.y) * scaleDelta / scale;
      
      setScale(nextScale);
      setTranslateX(prev => prev + adjustX);
      setTranslateY(prev => prev + adjustY);
      setLastDist(newDist);
      setLastCenter(newCenter);
    }
  }

  function onTouchEnd(e) {
    if (e.touches.length === 0) {
      setIsDragging(false);
      setLastDist(null);
      setLastCenter(null);
    } else if (e.touches.length === 1) {
      // Switched from two fingers to one - start dragging
      setLastDist(null);
      setIsDragging(true);
      const touch = e.touches[0];
      setLastCenter({ x: touch.pageX, y: touch.pageY });
    }
  }

  // Mouse events for desktop support
  function onMouseDown(e) {
    setIsDragging(true);
    setLastCenter({ x: e.pageX, y: e.pageY });
  }

  function onMouseMove(e) {
    if (!isDragging) return;
    
    const deltaX = e.pageX - lastCenter.x;
    const deltaY = e.pageY - lastCenter.y;
    
    setTranslateX(prev => prev + deltaX);
    setTranslateY(prev => prev + deltaY);
    setLastCenter({ x: e.pageX, y: e.pageY });
  }

  function onMouseUp() {
    setIsDragging(false);
    setLastCenter(null);
  }

  function onWheel(e) {
    e.preventDefault();
    
    const delta = -e.deltaY * 0.01;
    let nextScale = scale * (1 + delta);
    nextScale = Math.max(0.3, Math.min(nextScale, 5));
    
    // Zoom towards mouse position
    const containerRect = containerRef.current.getBoundingClientRect();
    const centerX = containerRect.left + containerRect.width / 2;
    const centerY = containerRect.top + containerRect.height / 2;
    
    const scaleDelta = nextScale - scale;
    const adjustX = (centerX - e.pageX) * scaleDelta / scale;
    const adjustY = (centerY - e.pageY) * scaleDelta / scale;
    
    setScale(nextScale);
    setTranslateX(prev => prev + adjustX);
    setTranslateY(prev => prev + adjustY);
  }

  return (
    <div
      ref={containerRef}
      style={{
        ...style,
        overflow: 'hidden',
        cursor: isDragging ? 'grabbing' : 'grab',
        touchAction: 'none',
        userSelect: 'none'
      }}
      onTouchStart={onTouchStart}
      onTouchMove={onTouchMove}
      onTouchEnd={onTouchEnd}
      onTouchCancel={onTouchEnd}
      onMouseDown={onMouseDown}
      onMouseMove={onMouseMove}
      onMouseUp={onMouseUp}
      onMouseLeave={onMouseUp}
      onWheel={onWheel}
    >
      <div
        style={{
          transform: `scale(${scale}) translate(${translateX}px, ${translateY}px)`,
          transformOrigin: 'center center',
          transition: isDragging || lastDist ? 'none' : 'transform 0.15s ease-out',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minWidth: '100%',
          minHeight: '100%'
        }}
      >
        {children}
      </div>
    </div>
  );
}